// macOS Finder-style inline previews for vault file cards.
import { useEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { Spinner } from "../ui/spinner";
import {
  fetchVaultItemAsFile,
  fetchVaultItemPreviewUrl,
  fetchVaultItemText,
  type VaultItem,
} from "../../morrowVault";
import { decodeVaultAudioWaveformPeaks } from "./vaultAudioWaveform";
import {
  iconForMime,
  isArchiveFile,
  isAudioMime,
  isHeicMime,
  isImageMime,
  isOfficeFile,
  isPdfMime,
  isPreviewableTextItem,
  isSpreadsheetFile,
  isStructuredTextMime,
  isTextMime,
  isVideoMime,
  resolveVaultMimeType,
  usesVaultTextSnippetPreview,
} from "./vaultShared";
import {
  convertHeicPreviewUrl,
  extractPdfTextSnippet,
  fetchVaultOfficeSnippet,
  fetchVaultPdfEmbedUrl,
  fetchVaultSpreadsheetSnippet,
  fetchVaultTextSnippet,
  renderVaultPdfThumbnail,
} from "./vaultDocumentPreview";

const TEXT_SNIPPET_MAX_CHARS = 2_000;

function PreviewShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "relative flex size-full min-h-0 min-w-0 items-center justify-center overflow-hidden bg-[#e8e8ed] dark:bg-muted/35",
        className,
      )}
    >
      {children}
    </div>
  );
}

function PreviewLoading({ className }: { className?: string }) {
  return (
    <PreviewShell className={className ?? ""}>
      <Spinner className="size-5 text-muted-foreground" />
    </PreviewShell>
  );
}

function FinderIconFallback({ mimeType }: { mimeType: string }) {
  const Icon = iconForMime(mimeType);
  return (
    <PreviewShell>
      <div className="flex size-[38%] min-h-10 min-w-10 max-h-14 max-w-14 items-center justify-center rounded-[18%] bg-white shadow-[0_3px_10px_rgba(0,0,0,0.12)] ring-1 ring-black/5 dark:bg-[#fcfcfc] dark:shadow-[0_3px_12px_rgba(0,0,0,0.35)]">
        <Icon className="size-[44%] text-muted-foreground" aria-hidden="true" />
      </div>
    </PreviewShell>
  );
}

function VaultBinaryPreview({
  item,
  mimeType,
  className,
  render,
}: {
  item: VaultItem;
  mimeType: string;
  className?: string;
  render: (objectUrl: string) => ReactNode;
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let createdUrl: string | null = null;
    setLoading(true);
    setObjectUrl(null);

    void fetchVaultItemPreviewUrl(item).then((url) => {
      if (!active) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      createdUrl = url;
      setObjectUrl(url);
      setLoading(false);
    });

    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [item.id]);

  if (loading) {
    return <PreviewLoading className={className ?? ""} />;
  }
  if (!objectUrl) {
    return <FinderIconFallback mimeType={mimeType} />;
  }

  return <PreviewShell className={className ?? ""}>{render(objectUrl)}</PreviewShell>;
}

function VaultTextSnippetPreview({
  item,
  mimeType,
  text,
}: {
  item: VaultItem;
  mimeType: string;
  text: string | null;
}) {
  if (!text || text.trim().length === 0) {
    return <FinderIconFallback mimeType={mimeType} />;
  }

  return (
    <PreviewShell className="bg-white dark:bg-[#fcfcfc]">
      <pre className="absolute inset-0 overflow-hidden whitespace-pre-wrap break-words p-2 font-sans text-[6px] leading-[8px] text-foreground/80 sm:text-[7px] sm:leading-[9px]">
        {text.trim()}
      </pre>
    </PreviewShell>
  );
}

function useVaultTextSnippet(item: VaultItem, mimeType: string) {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setText(null);

    void (async () => {
      let snippet: string | null = null;
      if (item.hasText) {
        const extracted = await fetchVaultItemText(item.id);
        snippet = extracted ? extracted.slice(0, TEXT_SNIPPET_MAX_CHARS) : null;
      } else if (isTextMime(mimeType) || isStructuredTextMime(mimeType)) {
        const file = await fetchVaultItemAsFile(item);
        if (file) {
          const raw = await file.text();
          snippet = raw.slice(0, TEXT_SNIPPET_MAX_CHARS);
        }
      } else if (isPreviewableTextItem(item)) {
        snippet = await fetchVaultTextSnippet(item);
      }

      if (!active) return;
      setText(snippet);
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [item, mimeType]);

  return { text, loading };
}

function VaultTextPreview({ item, mimeType }: { item: VaultItem; mimeType: string }) {
  const { text, loading } = useVaultTextSnippet(item, mimeType);
  if (loading) return <PreviewLoading className="bg-white dark:bg-[#fcfcfc]" />;
  return <VaultTextSnippetPreview item={item} mimeType={mimeType} text={text} />;
}

function VaultHeicPreview({ item, mimeType }: { item: VaultItem; mimeType: string }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let createdUrl: string | null = null;
    setLoading(true);
    setObjectUrl(null);

    void (async () => {
      const converted = await convertHeicPreviewUrl(item);
      if (!active) {
        if (converted) URL.revokeObjectURL(converted);
        return;
      }
      if (converted) {
        createdUrl = converted;
        setObjectUrl(converted);
        setLoading(false);
        return;
      }

      const fallback = await fetchVaultItemPreviewUrl(item);
      if (!active) {
        if (fallback) URL.revokeObjectURL(fallback);
        return;
      }
      createdUrl = fallback;
      setObjectUrl(fallback);
      setLoading(false);
    })();

    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [item.id]);

  if (loading) return <PreviewLoading />;
  if (!objectUrl) return <FinderIconFallback mimeType={mimeType} />;

  return (
    <PreviewShell>
      <img src={objectUrl} alt={item.name} className="max-h-full max-w-full object-contain" />
    </PreviewShell>
  );
}

function VaultImagePreview({ item, mimeType }: { item: VaultItem; mimeType: string }) {
  if (isHeicMime(mimeType)) {
    return <VaultHeicPreview item={item} mimeType={mimeType} />;
  }

  return (
    <VaultBinaryPreview
      item={item}
      mimeType={mimeType}
      render={(objectUrl) => (
        <img src={objectUrl} alt={item.name} className="max-h-full max-w-full object-contain" />
      )}
    />
  );
}

type PdfPreviewMode = "loading" | "image" | "embed" | "text" | "icon";

function VaultPdfPreview({ item, mimeType }: { item: VaultItem; mimeType: string }) {
  const [mode, setMode] = useState<PdfPreviewMode>("loading");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const embedUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    setMode("loading");
    setImageUrl(null);
    setText(null);
    if (embedUrlRef.current) {
      URL.revokeObjectURL(embedUrlRef.current);
      embedUrlRef.current = null;
    }
    setEmbedUrl(null);

    void (async () => {
      const thumb = await renderVaultPdfThumbnail(item);
      if (!active) return;
      if (thumb) {
        setImageUrl(thumb);
        setMode("image");
        return;
      }

      if (item.hasText) {
        const extracted = await fetchVaultItemText(item.id);
        if (!active) return;
        if (extracted?.trim()) {
          setText(extracted.slice(0, TEXT_SNIPPET_MAX_CHARS));
          setMode("text");
          return;
        }
      }

      const snippet = await extractPdfTextSnippet(item);
      if (!active) return;
      if (snippet?.trim()) {
        setText(snippet);
        setMode("text");
        return;
      }

      const blobUrl = await fetchVaultPdfEmbedUrl(item);
      if (!active) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        return;
      }
      if (blobUrl) {
        embedUrlRef.current = blobUrl;
        setEmbedUrl(blobUrl);
        setMode("embed");
        return;
      }

      setMode("icon");
    })();

    return () => {
      active = false;
      if (embedUrlRef.current) {
        URL.revokeObjectURL(embedUrlRef.current);
        embedUrlRef.current = null;
      }
    };
  }, [item.id]);

  if (mode === "loading") {
    return <PreviewLoading className="bg-white dark:bg-[#fcfcfc]" />;
  }
  if (mode === "image" && imageUrl) {
    return (
      <PreviewShell className="bg-white dark:bg-[#fcfcfc]">
        <img
          src={imageUrl}
          alt=""
          className="max-h-full max-w-full object-contain"
          draggable={false}
        />
      </PreviewShell>
    );
  }
  if (mode === "text") {
    return <VaultTextSnippetPreview item={item} mimeType={mimeType} text={text} />;
  }
  if (mode === "embed" && embedUrl) {
    return (
      <PreviewShell className="bg-white dark:bg-[#fcfcfc]">
        <embed src={embedUrl} type="application/pdf" className="size-full min-h-0 border-0" />
      </PreviewShell>
    );
  }
  return <FinderIconFallback mimeType={mimeType} />;
}

function VaultSpreadsheetPreview({ item, mimeType }: { item: VaultItem; mimeType: string }) {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setText(null);

    void (async () => {
      let snippet: string | null = null;
      if (item.hasText) {
        const extracted = await fetchVaultItemText(item.id);
        snippet = extracted ? extracted.slice(0, TEXT_SNIPPET_MAX_CHARS) : null;
      }
      if (!snippet) {
        snippet = await fetchVaultSpreadsheetSnippet(item);
      }

      if (!active) return;
      setText(snippet);
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [item.id]);

  if (loading) {
    return <PreviewLoading className="bg-white dark:bg-[#fcfcfc]" />;
  }
  return <VaultTextSnippetPreview item={item} mimeType={mimeType} text={text} />;
}

function VaultOfficePreview({ item, mimeType }: { item: VaultItem; mimeType: string }) {
  const [text, setText] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const imageUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setText(null);
    if (imageUrlRef.current) {
      URL.revokeObjectURL(imageUrlRef.current);
      imageUrlRef.current = null;
    }
    setImageUrl(null);

    void (async () => {
      let snippet: string | null = null;
      if (item.hasText) {
        const extracted = await fetchVaultItemText(item.id);
        snippet = extracted ? extracted.slice(0, TEXT_SNIPPET_MAX_CHARS) : null;
      }
      if (!snippet) {
        const office = await fetchVaultOfficeSnippet(item);
        if (!active) return;
        if (office?.kind === "image") {
          imageUrlRef.current = office.url;
          setImageUrl(office.url);
          setLoading(false);
          return;
        }
        snippet = office?.kind === "text" ? office.text : null;
      }

      if (!active) return;
      setText(snippet);
      setLoading(false);
    })();

    return () => {
      active = false;
      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current);
        imageUrlRef.current = null;
      }
    };
  }, [item.id]);

  if (loading) {
    return <PreviewLoading className="bg-white dark:bg-[#fcfcfc]" />;
  }
  if (imageUrl) {
    return (
      <PreviewShell className="bg-white dark:bg-[#fcfcfc]">
        <img
          src={imageUrl}
          alt=""
          className="max-h-full max-w-full object-contain"
          draggable={false}
        />
      </PreviewShell>
    );
  }
  return <VaultTextSnippetPreview item={item} mimeType={mimeType} text={text} />;
}

function VaultArchivePreview({ item, mimeType }: { item: VaultItem; mimeType: string }) {
  const { text, loading } = useVaultTextSnippet(item, mimeType);
  if (loading) return <PreviewLoading className="bg-white dark:bg-[#fcfcfc]" />;
  return <VaultTextSnippetPreview item={item} mimeType={mimeType} text={text} />;
}

function VaultVideoPreview({ item, mimeType }: { item: VaultItem; mimeType: string }) {
  return (
    <VaultBinaryPreview
      item={item}
      mimeType={mimeType}
      className="bg-black"
      render={(objectUrl) => (
        <video
          src={objectUrl}
          muted
          playsInline
          preload="metadata"
          className="max-h-full max-w-full object-contain"
          aria-hidden="true"
        />
      )}
    />
  );
}

function VaultAudioWaveformBars({ peaks }: { peaks: readonly number[] }) {
  return (
    <div
      className="flex h-[42%] w-[80%] max-w-40 items-center justify-center gap-[2px]"
      aria-hidden="true"
    >
      {peaks.map((peak, index) => (
        <span
          key={index}
          className="min-h-[10%] flex-1 rounded-full bg-muted-foreground/50"
          style={{ height: `${Math.round(12 + peak * 88)}%` }}
        />
      ))}
    </div>
  );
}

function VaultAudioPreview({ item, mimeType }: { item: VaultItem; mimeType: string }) {
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPeaks(null);

    void (async () => {
      const file = await fetchVaultItemAsFile(item);
      if (!active) return;
      if (!file) {
        setPeaks(null);
        setLoading(false);
        return;
      }

      const buffer = await file.arrayBuffer();
      if (!active) return;
      const decoded = await decodeVaultAudioWaveformPeaks(buffer);
      if (!active) return;
      setPeaks(decoded);
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [item.id]);

  if (loading) {
    return <PreviewLoading className="bg-white dark:bg-[#fcfcfc]" />;
  }
  if (!peaks || peaks.length === 0) {
    return <FinderIconFallback mimeType={mimeType} />;
  }

  return (
    <PreviewShell className="bg-white dark:bg-[#fcfcfc]">
      <VaultAudioWaveformBars peaks={peaks} />
    </PreviewShell>
  );
}

export function VaultCardPreview({ item }: { item: VaultItem }) {
  const mimeType = resolveVaultMimeType(item);

  if (isImageMime(mimeType)) {
    return (
      <div className="size-full min-h-0 min-w-0">
        <VaultImagePreview item={item} mimeType={mimeType} />
      </div>
    );
  }

  if (isVideoMime(mimeType)) {
    return (
      <div className="size-full min-h-0 min-w-0">
        <VaultVideoPreview item={item} mimeType={mimeType} />
      </div>
    );
  }

  if (isAudioMime(mimeType)) {
    return (
      <div className="size-full min-h-0 min-w-0">
        <VaultAudioPreview item={item} mimeType={mimeType} />
      </div>
    );
  }

  if (isPdfMime(mimeType)) {
    return (
      <div className="size-full min-h-0 min-w-0">
        <VaultPdfPreview item={item} mimeType={mimeType} />
      </div>
    );
  }

  if (isSpreadsheetFile(item)) {
    return (
      <div className="size-full min-h-0 min-w-0">
        <VaultSpreadsheetPreview item={item} mimeType={mimeType} />
      </div>
    );
  }

  if (isOfficeFile(item)) {
    return (
      <div className="size-full min-h-0 min-w-0">
        <VaultOfficePreview item={item} mimeType={mimeType} />
      </div>
    );
  }

  if (isArchiveFile(item)) {
    return (
      <div className="size-full min-h-0 min-w-0">
        <VaultArchivePreview item={item} mimeType={mimeType} />
      </div>
    );
  }

  if (usesVaultTextSnippetPreview(item)) {
    return (
      <div className="size-full min-h-0 min-w-0">
        <VaultTextPreview item={item} mimeType={mimeType} />
      </div>
    );
  }

  return (
    <div className="size-full min-h-0 min-w-0">
      <FinderIconFallback mimeType={mimeType} />
    </div>
  );
}
